/**
 * RequireAdmin Component
 * Route guard that checks if user has admin role
 * Redirects to listings home if not admin
 */

import { Navigate, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

interface RequireAdminProps {
  children?: React.ReactNode
  showAccessDenied?: boolean
}

export function RequireAdmin({ children, showAccessDenied = false }: RequireAdminProps) {
  const { user, isInitialized } = useAuth()

  // Wait for auth initialization
  if (!isInitialized) {
    return null
  }

  // Check if user has admin role
  const isAdmin = user?.role === 'admin'

  if (!isAdmin) {
    // Show access denied page or redirect
    if (showAccessDenied) {
      return (
        <div className={cn(
          'min-h-screen bg-[#ffffff]',
          'flex items-center justify-center p-4'
        )}>
          <Card className="w-full max-w-md border-[#e5e5e5]">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#f5f5f5] flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-[#000000]" />
              </div>
              <CardTitle className="text-xl font-bold text-[#000000]">
                Access Denied
              </CardTitle>
              <CardDescription className="text-[#666666]">
                You don't have permission to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-[#666666] mb-6">
                This area is restricted to administrators only. 
                If you believe this is an error, please contact your system administrator.
              </p>
                <Button
                  onClick={() => window.location.href = '/'}
                  className="bg-[#000000] hover:bg-[#1a1a1a]"
                >
                  Go to Listings
                </Button>
            </CardContent>
          </Card>
        </div>
      )
    }

    // Redirect to listings home
    return <Navigate to="/" replace />
  }

  // Render children or outlet for nested routes
  return children ? <>{children}</> : <Outlet />
}

export default RequireAdmin
